import PropTypes from 'prop-types';
import { useState } from 'react';
import { useParams } from 'react-router';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import { patchNode } from '@/api/nodes';
import { caseActions } from '@/stores/caseStore';
import { nodeActions } from '@/stores/nodeStore';

const BetterCostDataModal = ({
  show,
  currentSource,
  suggestedSource,
  dataviewerInfo,
  resetParams = false,
  onDismiss,
  onComplete,
}) => {
  const { pathwayId, caseId, nodeId } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  if (!show) return null;

  const handleUpdate = async () => {
    setIsLoading(true);
    try {
      const updates = { params_source: suggestedSource };
      if (resetParams) updates.reset_params = true;
      await patchNode(caseId, nodeId, updates);
      await Promise.all([nodeActions.init(pathwayId, caseId, nodeId), caseActions.recheckBalanceAndAnalysis()]);
    } finally {
      setIsLoading(false);
    }
    onComplete();
  };

  const handleKeep = async () => {
    setIsLoading(true);
    try {
      await nodeActions.init(pathwayId, caseId, nodeId);
    } finally {
      setIsLoading(false);
    }
    onComplete();
  };

  const handleSelectFromViewer = () => {
    const query = dataviewerInfo ? `?category=${dataviewerInfo.category}&module=${dataviewerInfo.slug}` : '';
    window.open(`/dataviewer${query}`, '_blank', 'noopener,noreferrer');
    onDismiss();
  };

  return (
    <dialog open={show} className="modal">
      <div className="modal-box max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-50 p-2 rounded-full shrink-0">
            <SparklesIcon className="w-6 h-6 text-blue-500" />
          </div>
          <h3 className="font-bold text-lg leading-tight">Regional Dataset Found</h3>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          A dataset with better regional coverage has been found for your location and startup year.
        </p>
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex items-center gap-3 mb-5">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-medium text-gray-400 mb-1">Current</div>
            <div className="font-semibold text-sm text-gray-600">{currentSource || '—'}</div>
          </div>
          <ArrowRightIcon className="w-4 h-4 text-gray-300 shrink-0" />
          <div className="flex-1 min-w-0 text-right">
            <div className="text-[10px] uppercase tracking-wide font-medium text-blue-400 mb-1">Suggested</div>
            <div className="font-semibold text-sm text-blue-600">{suggestedSource}</div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleUpdate}
            disabled={isLoading}
            className="btn btn-sm bg-blue-500 hover:bg-blue-600 text-white border-none w-full"
          >
            Update to {suggestedSource}
          </button>
          <button
            type="button"
            onClick={handleKeep}
            disabled={isLoading}
            className="btn btn-sm btn-outline border-gray-300 text-gray-600 hover:bg-gray-100 hover:border-gray-400 w-full"
          >
            Keep current defaults
          </button>
          <button
            type="button"
            onClick={handleSelectFromViewer}
            disabled={isLoading}
            className="btn btn-ghost btn-sm text-gray-400 w-full hover:text-gray-600"
          >
            View in Data Viewer
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={handleKeep}></div>
    </dialog>
  );
};

BetterCostDataModal.propTypes = {
  show: PropTypes.bool.isRequired,
  currentSource: PropTypes.string,
  suggestedSource: PropTypes.string.isRequired,
  dataviewerInfo: PropTypes.shape({
    category: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }),
  resetParams: PropTypes.bool,
  onDismiss: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default BetterCostDataModal;
